import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Cable,
    Megaphone,
    Package,
    PackagePlus,
    Search,
    WalletCards,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FloatingActionsMenu from "../components/common/FloatingActionsMenu";
import DashboardPlanUsageCard from "../components/dashboard/DashboardPlanUsageCard";
import DashboardRecentRow from "../components/dashboard/DashboardRecentRow";
import DashboardRecentSection from "../components/dashboard/DashboardRecentSection";
import DashboardRecentTabs, {
    DASHBOARD_RECENT_TAB_KEYS,
} from "../components/dashboard/DashboardRecentTabs";
import DashboardStatCard from "../components/dashboard/DashboardStatCard";
import DashboardStatusBadge from "../components/dashboard/DashboardStatusBadge";
import CampaignStatusBadge from "../components/campaigns/CampaignStatusBadge";
import PageContainer from "../components/layout/PageContainer";
import { ROUTES } from "../config/constants";
import { fetchCurrentUsage } from "../store/authSlice";
import { fetchSearchHistory } from "../store/leadsSlice";
import { fetchOutreachCampaigns } from "../store/outreachSlice";
import { fetchProducts } from "../store/productsSlice";
import {
    clampPercentage,
    formatCampaignDate,
} from "../utils/campaignDisplayUtils";
import { formatSearchHistoryDate } from "../utils/formatDate";
import { formatCreditValue, getCreditUsage } from "../utils/subscription";
import useElementInView from "../hooks/useElementInView";

const getDateTime = (value) => {
    if (!value) return 0;

    const timestamp = new Date(value).getTime();

    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getProductCampaignCount = (product) => {
    const count = Number(product?.dependent_sequence_count);

    return Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
};

const DASHBOARD_RECENT_SECTION_HEIGHT = "h-[420px]";

const Dashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const topActionsRef = useRef(null);
    const areTopActionsVisible = useElementInView(topActionsRef, {
        rootMargin: "-64px 0px 0px 0px",
        threshold: 0.9,
        minimumVisibleRatio: 0.9,
        topOffset: 64,
    });
    const [activeRecentTab, setActiveRecentTab] = useState(
        DASHBOARD_RECENT_TAB_KEYS.SEARCHES,
    );
    const { user, credits } = useSelector((state) => state.auth);
    const { searchHistory, searchHistoryStatus, searchHistoryError } =
        useSelector((state) => state.leads);
    const { products, productsStatus, productsError } = useSelector(
        (state) => state.products,
    );
    const { campaigns, dashboardStatus, dashboardError } = useSelector(
        (state) => state.outreach,
    );

    useEffect(() => {
        dispatch(fetchCurrentUsage());
        dispatch(fetchSearchHistory());
        dispatch(fetchProducts());
        dispatch(fetchOutreachCampaigns());
    }, [dispatch]);

    const activeCampaignCount = useMemo(() => {
        if (dashboardStatus !== "succeeded" || !Array.isArray(campaigns)) {
            return "--";
        }

        return campaigns.filter(
            (campaign) =>
                String(campaign?.status || "")
                    .trim()
                    .toLowerCase() === "active",
        ).length;
    }, [campaigns, dashboardStatus]);

    const recentSearches = useMemo(() => {
        if (!Array.isArray(searchHistory)) return [];

        return [...searchHistory].sort(
            (first, second) =>
                getDateTime(second?.created_at) -
                getDateTime(first?.created_at),
        );
    }, [searchHistory]);

    const recentCampaigns = useMemo(() => {
        if (!Array.isArray(campaigns)) return [];

        return [...campaigns].sort(
            (first, second) =>
                getDateTime(second?.created_at) -
                getDateTime(first?.created_at),
        );
    }, [campaigns]);

    const recentProducts = useMemo(() => {
        if (!Array.isArray(products)) return [];

        return [...products].sort((first, second) => {
            const firstDate = first?.updated_at || first?.created_at;
            const secondDate = second?.updated_at || second?.created_at;

            return getDateTime(secondDate) - getDateTime(firstDate);
        });
    }, [products]);

    const searchLeadUsage = credits
        ? getCreditUsage({
              used: credits?.search_leads_used,
              limit: credits?.search_lead_limit,
              remaining: credits?.search_leads_remaining,
          })
        : null;
    const remainingSearchCredits = searchLeadUsage
        ? formatCreditValue(searchLeadUsage.remaining)
        : "";
    const totalSearchCredits = searchLeadUsage
        ? formatCreditValue(searchLeadUsage.limit)
        : "";
    const remainingSearchCreditsValue = searchLeadUsage
        ? (
              <>
                  <span className="sm:hidden">
                      {remainingSearchCredits}/{totalSearchCredits}
                  </span>
                  <span className="hidden sm:inline">
                      {remainingSearchCredits} out of {totalSearchCredits}
                  </span>
              </>
          )
        : "--";
    const displayName = user?.full_name || user?.email || "User";

    const stats = [
        {
            label: "Total Searches",
            value:
                searchHistoryStatus === "succeeded" &&
                Array.isArray(searchHistory)
                    ? searchHistory.length
                    : "--",
            icon: Search,
            variant: "indigo",
            onClick: () => navigate(ROUTES.SEARCH),
        },
        {
            label: "Total Products",
            value:
                productsStatus === "succeeded" && Array.isArray(products)
                    ? products.length
                    : "--",
            icon: Package,
            variant: "violet",
            onClick: () => navigate(ROUTES.PRODUCTS),
        },
        {
            label: "Total Active Campaigns",
            value: activeCampaignCount,
            icon: Megaphone,
            variant: "emerald",
            onClick: () => navigate(ROUTES.CAMPAIGNS),
        },
        {
            label: "Remaining Search Credits",
            value: remainingSearchCreditsValue,
            icon: WalletCards,
            variant: "amber",
        },
    ];

    const quickActions = [
        {
            label: "Create Search",
            icon: Search,
            to: ROUTES.SEARCH,
        },
        {
            label: "Create Product",
            icon: PackagePlus,
            onClick: () =>
                navigate(ROUTES.PRODUCTS, {
                    state: { openCreateProduct: true },
                }),
        },
        {
            label: "Create Campaign",
            icon: Megaphone,
            to: ROUTES.CAMPAIGNS,
        },
        {
            label: "Create Connection",
            icon: Cable,
            to: ROUTES.CONNECTIONS,
        },
    ];
    return (
        <main className="min-h-screen bg-[#F8F8FE]">
            <PageContainer className="py-4 pb-20 sm:pb-20">
                <header className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                            Dashboard
                        </h1>
                            <p className="mt-2 max-w-2xl truncate text-sm text-slate-600">
                                Welcome back,{" "}
                                <span className="font-semibold text-indigo-700">
                                {displayName}
                            </span>
                            </p>    
                    </div>

                    <div
                        ref={topActionsRef}
                        className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-start lg:ml-auto lg:justify-end"
                    >
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.PRODUCTS)}
                            className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 sm:flex-none sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Package size={16} aria-hidden="true" />
                            <span className="sm:hidden">Products</span>
                            <span className="hidden sm:inline">
                                My Products
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.CAMPAIGNS)}
                            className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 sm:flex-none sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Megaphone size={16} aria-hidden="true" />
                            <span className="sm:hidden">Campaigns</span>
                            <span className="hidden sm:inline">
                                My Campaigns
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.SEARCH)}
                            className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-3 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 sm:flex-none sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Search size={16} aria-hidden="true" />
                            <span className="sm:hidden">Searches</span>
                            <span className="hidden sm:inline">
                                Search Leads
                            </span>
                        </button>
                    </div>
                </header>

                <section
                    aria-label="Dashboard summary"
                    className="grid grid-cols-2 gap-3 lg:grid-cols-4"
                >
                    {stats.map((stat) => (
                        <DashboardStatCard key={stat.label} {...stat} />
                    ))}
                </section>

                <DashboardRecentTabs
                    activeTab={activeRecentTab}
                    onTabChange={setActiveRecentTab}
                    className="mt-6"
                />

                <div className="mt-4 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
                    <div className="flex min-w-0 flex-col gap-5">
                        {activeRecentTab ===
                            DASHBOARD_RECENT_TAB_KEYS.SEARCHES && (
                            <DashboardRecentSection
                                title="Recent Searches"
                                icon={Search}
                                items={recentSearches}
                                status={searchHistoryStatus}
                                error={searchHistoryError}
                                errorMessage="Unable to load recent searches."
                                emptyTitle="No searches yet."
                                emptyDescription="Run a lead search to see it here."
                                scrollAllItems
                                heightClassName={DASHBOARD_RECENT_SECTION_HEIGHT}
                                getItemKey={(search, index) =>
                                    search?.id || `search-${index}`
                                }
                                renderItem={(search) => (
                                    <DashboardRecentRow
                                        onClick={() =>
                                            navigate(ROUTES.SEARCH, {
                                                state: {
                                                    activateSearch: {
                                                        id: search?.id || null,
                                                        queryString:
                                                            search?.query_string ||
                                                            "",
                                                    },
                                                },
                                            })
                                        }
                                        ariaLabel={`Open search ${search?.query_string || "history"}`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-semibold text-slate-900">
                                                {search?.query_string ||
                                                    "Untitled search"}
                                            </p>
                                            <p className="mt-1 text-xs text-slate-500">
                                                {formatSearchHistoryDate(
                                                    search?.created_at,
                                                )}
                                            </p>
                                        </div>

                                        <DashboardStatusBadge
                                            status={search?.status}
                                        />
                                    </DashboardRecentRow>
                                )}
                            />
                        )}

                        {activeRecentTab ===
                            DASHBOARD_RECENT_TAB_KEYS.PRODUCTS && (
                            <DashboardRecentSection
                                title="Recent Products"
                                icon={Package}
                                items={recentProducts}
                                status={productsStatus}
                                error={productsError}
                                errorMessage="Unable to load recent products."
                                emptyTitle="No products yet."
                                emptyDescription="Create a product to start building campaigns."
                                scrollAllItems
                                heightClassName={DASHBOARD_RECENT_SECTION_HEIGHT}
                                getItemKey={(product, index) =>
                                    product?.id || `product-${index}`
                                }
                                renderItem={(product) => {
                                    const campaignCount =
                                        getProductCampaignCount(product);
                                    const productDate =
                                        product?.updated_at ||
                                        product?.created_at;

                                    return (
                                        <DashboardRecentRow
                                            onClick={() =>
                                                navigate(ROUTES.PRODUCTS, {
                                                    state: {
                                                        selectedProduct:
                                                            product,
                                                        selectedProductId:
                                                            product?.id ||
                                                            product?.product_id ||
                                                            product?.productId ||
                                                            "",
                                                    },
                                                })
                                            }
                                            ariaLabel={`Open product ${product?.name || "details"}`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold text-slate-900">
                                                    {product?.name ||
                                                        "Untitled product"}
                                                </p>
                                                {product?.description && (
                                                    <p className="mt-1 line-clamp-1 text-xs leading-5 text-slate-500">
                                                        {product.description}
                                                    </p>
                                                )}
                                                <p className="mt-1 text-xs text-slate-500">
                                                    {product?.updated_at
                                                        ? "Updated"
                                                        : "Created"}{" "}
                                                    {formatCampaignDate(
                                                        productDate,
                                                    )}
                                                </p>
                                            </div>

                                            <span className="inline-flex w-fit shrink-0 items-center whitespace-nowrap rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                                                Used by {campaignCount}{" "}
                                                {campaignCount === 1
                                                    ? "campaign"
                                                    : "campaigns"}
                                            </span>
                                        </DashboardRecentRow>
                                    );
                                }}
                            />
                        )}

                        {activeRecentTab ===
                            DASHBOARD_RECENT_TAB_KEYS.CAMPAIGNS && (
                            <DashboardRecentSection
                                title="Recent Campaigns"
                                icon={Megaphone}
                                items={recentCampaigns}
                                status={dashboardStatus}
                                error={dashboardError}
                                errorMessage="Unable to load recent campaigns."
                                emptyTitle="No campaigns yet."
                                emptyDescription="Create a campaign from your lead results."
                                scrollAllItems
                                heightClassName={DASHBOARD_RECENT_SECTION_HEIGHT}
                                getItemKey={(campaign, index) =>
                                    campaign?.sequence_id ||
                                    `campaign-${index}`
                                }
                                renderItem={(campaign) => {
                                    const progress = clampPercentage(
                                        campaign?.ui_helper_context
                                            ?.progress_percentage,
                                    );

                                    return (
                                        <DashboardRecentRow
                                            onClick={() =>
                                                navigate(ROUTES.CAMPAIGNS, {
                                                    state: {
                                                        selectedCampaign:
                                                            campaign,
                                                        selectedCampaignId:
                                                            campaign?.sequence_id ||
                                                            campaign?.id ||
                                                            "",
                                                    },
                                                })
                                            }
                                            ariaLabel={`Open campaign ${campaign?.name || "details"}`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold text-slate-900">
                                                    {campaign?.name ||
                                                        "Untitled campaign"}
                                                </p>
                                                <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                                                    <span className="max-w-full truncate">
                                                        Product:{" "}
                                                        {campaign?.product_name ||
                                                            "Unavailable"}
                                                    </span>
                                                    <span aria-hidden="true">
                                                        ·
                                                    </span>
                                                    <span>{progress}%</span>
                                                </div>
                                                <p className="mt-1 truncate text-xs text-slate-500">
                                                    Search:{" "}
                                                    {campaign?.search_query ||
                                                        "Unavailable"}{" "}
                                                    ·{" "}
                                                    {formatCampaignDate(
                                                        campaign?.created_at,
                                                    )}
                                                </p>
                                            </div>

                                            <CampaignStatusBadge
                                                status={campaign?.status}
                                                campaign={campaign}
                                                isPaused={
                                                    campaign?.ui_helper_context
                                                        ?.is_paused
                                                }
                                            />
                                        </DashboardRecentRow>
                                    );
                                }}
                            />
                        )}
                    </div>

                    <div className="flex min-w-0 flex-col gap-5">
                        <DashboardPlanUsageCard
                            credits={credits}
                            heightClassName={DASHBOARD_RECENT_SECTION_HEIGHT}
                        />
                    </div>
                </div>

            </PageContainer>
            <FloatingActionsMenu
                actions={quickActions}
                visible={!areTopActionsVisible}
            />
        </main>
    );
};

export default Dashboard;
